import { IAlbum } from 'ngx-lightbox';
import AssetHelper, { FORM_SECTIONS } from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Product from 'src/app/model/product/product';
import { SubProject } from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import RegisteredActionType, {
  REGISTERED_ACTION_TYPES
} from 'src/app/model/registered-action-type';
import { ExplicitlyCompletableTask } from 'src/app/model/task-list/explicitly-completable-task';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { SubProjectTask } from 'src/app/model/task-list/subproject-task';
import { TASK_TYPES } from 'src/app/model/task-list/task';
import { TimeBudgettedTask } from 'src/app/model/task-list/time-budgetted-task';
import { User } from 'src/app/model/user';
import { OfflineProjectStorageService } from 'src/app/services/offline-project-storage.service';

export default class GeneralPlacerFormHelper {
  private constructor() {}

  public static STORE_OFFLINE_CHANGE_DELAY = 1000;

  public static async loadProjectForTask(
    task: SubProjectTask | LoadTask,
    offlineProjectStorageService: OfflineProjectStorageService
  ): Promise<void> {
    if (task.subProject == null) {
      throw new Error('task.subProject cannot be null');
    }
    if (task.subProject.project == null) {
      throw new Error('task.subProject.project cannot be null');
    }

    const project = await offlineProjectStorageService
      .getProject(task.subProject.project.id)
      .toPromise();
    if (project == null) {
      throw new Error(
        `Could not load project with id ${task.subProject.project.id}`
      );
    }

    const subProject = !CollectionHelper.isPopulated(project.subProjects)
      ? null
      : project.subProjects.find(sub => sub.id === task.subProject.id);
    if (subProject == null) {
      throw new Error(
        `Could not find subproject with id ${task.subProject.id}`
      );
    }

    subProject.project = project;
    task.subProject = subProject;

    if (task.type === TASK_TYPES.LOAD_TASK) {
      this.setProductForTask(task);
    }
  }

  public static getBannerText(subProject: SubProject): string {
    const toReturn = ProjectHelper.getType(subProject, null, false);
    return toReturn === 'Plaatsing' ? 'Te plaatsen' : toReturn;
  }

  public static getBannerClass(subProject: SubProject): string {
    const toReturn = ProjectHelper.getTypeStatusString(subProject, null, false);
    return toReturn === 'plaatsing' ? 'plaatsing-banner' : toReturn;
  }

  public static getTimeBudget(task: TimeBudgettedTask): string {
    return task.minuteBudget == null ? null : `${task.minuteBudget} minuten`;
  }

  private static setProductForTask(task: LoadTask): void {
    if (task.product == null) {
      return;
    }

    const matchingProduct = CollectionHelper.isPopulated(
      task.subProject.project.products
    )
      ? task.subProject.project.products.find(p => p.id === task.product.id)
      : null;

    if (matchingProduct == null) {
      throw new Error(
        `Product with id ${task.product.id} not found in project`
      );
    }

    task.product = matchingProduct;
  }

  public static getSketchupAlbum(product: Product): IAlbum[] {
    return CollectionHelper.isPopulated(product.productAssets)
      ? product.productAssets
          .filter(
            pa => !pa.archived && pa.formSection === FORM_SECTIONS.SKETCHUP
          )
          .map(pa => AssetHelper.toIAlbum(pa))
      : null;
  }

  /**
   * Precondition: if task.registeredActions != null, then registeredActions is sorted in descending order according to date
   */
  public static isTaskStarted(task: ExplicitlyCompletableTask): boolean {
    if (!CollectionHelper.isPopulated(task.registeredActions)) {
      return false;
    }
    for (const registeredAction of task.registeredActions) {
      if (registeredAction.type == null) {
        continue;
      }
      if (
        registeredAction.type.ordinal ===
        REGISTERED_ACTION_TYPES.START_PROJECT_EXECUTION.ordinal
      ) {
        return true;
      }
      if (
        registeredAction.type.ordinal ===
        REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION.ordinal
      ) {
        return false;
      }
    }
    return false;
  }

  public static addTaskRegisteredAction(
    task: ExplicitlyCompletableTask,
    type: RegisteredActionType,
    user: User
  ): void {
    if (task.registeredActions == null) {
      task.registeredActions = [];
    }

    task.registeredActions.unshift(
      GeneralHelper.initializeRegisteredAction(user, new Date(), type, true)
    );
  }

  public static getTaskExecutedTimeString(
    registeredActions: RegisteredAction[]
  ): string {
    return `Uitgevoerd in ${GeneralHelper.getMinutesInHourAndMinutes(
      GeneralHelper.getTotalExecutionMinutes(registeredActions)
    )}`;
  }
}
